import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { recruiterLogin } from '../../api/recruiterAuth';
import AnimatedBackground from '../components/AnimatedBackground';
import { recruiterAuth } from '../recruiterAuth';
import styles from '../authPages.module.css';

export default function RecruiterLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (recruiterAuth.isLoggedIn()) {
    return <Navigate to="/recruiter/search" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!email.trim() || !password) {
        setError('Email and password are required.');
        return;
      }
      const data = await recruiterLogin({
        email: email.trim().toLowerCase(),
        password,
      });
      const token = data.accessToken || data.token;
      if (!token) {
        setError('Login succeeded but no token was returned.');
        return;
      }
      recruiterAuth.setToken(token);
      navigate('/recruiter/search', { replace: true });
    } catch (err) {
      setError(err?.message || 'Sign in failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className={styles.page}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <AnimatedBackground />

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <div className={styles.brandRow}>
          <span className={styles.logoMark} aria-hidden="true">
            S
          </span>
          <span className={styles.portalLabel}>Recruiter Portal</span>
        </div>

        <h1 className={styles.title}>Welcome back</h1>
        <p className={styles.subtitle}>Search and connect with AI-verified talent</p>

        {error ? (
          <div className={styles.error} role="alert">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recruiter-email">
              Email
            </label>
            <input
              id="recruiter-email"
              className={styles.input}
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="recruiter-password">
              Password
            </label>
            <input
              id="recruiter-password"
              className={styles.input}
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <motion.button
            type="submit"
            className={styles.submit}
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </motion.button>
        </form>

        <p className={styles.footer}>
          Don&apos;t have an account? <Link to="/recruiter/signup">Sign up</Link>
        </p>
      </motion.div>
    </motion.div>
  );
}
